/** @file nodeEntries: the rows of a node menu, shared by a viewport joint and a tree row. */
import { descendantsOf } from '../../panels/viewport/descendantsOf';
import { keyFrame } from '../../panels/timeline/keyFrame';
import { removeNode } from '../../panels/tree/removeNode';
import { addChild } from './addChild';
import { firstDrawable } from './firstDrawable';
import { frameSelectionEvent } from './frameSelectionEvent';
import { mirrorPairOf } from './mirrorPairOf';
import { mirrorToTwin } from './mirrorToTwin';
import { nodeTargets } from './nodeTargets';
import type { MenuEntry, NodeMenuCtx } from './registryTypes';
import { resetToRest } from './resetToRest';

type Entry = MenuEntry<NodeMenuCtx>;
const SEP: Entry = { kind: 'separator' };
const twinOf = (c: NodeMenuCtx): string | undefined => c.store.getState().puppet.doc?.puppet.nodes[c.node]?.mirror;

const KEYING: Entry[] = [
  {
    kind: 'item', id: 'key.joint', label: () => 'Key this joint', chords: ['transport.keyJoint'],
    run: (c) => { keyFrame(c.store, c.store.getState().playhead.frame, nodeTargets(c)); },
  },
  {
    kind: 'item', id: 'key.pose', label: () => 'Key whole pose', chords: ['transport.keyAll'],
    run: (c) => { keyFrame(c.store, c.store.getState().playhead.frame); },
  },
  { kind: 'item', id: 'pose.rest', label: () => 'Reset to rest', chords: ['viewport.pose.rest'], run: (c) => { resetToRest(c.store, nodeTargets(c)); } },
  {
    kind: 'item', id: 'pose.mirror', label: () => 'Mirror to twin', chords: ['viewport.pose.mirror'], disabled: (c) => twinOf(c) === undefined,
    run: (c) => { mirrorToTwin(c.store, nodeTargets(c)); },
  },
  {
    kind: 'item', id: 'node.mirrorPair', label: (c) => mirrorPairOf(c.store, c.node).label, chords: ['tree.mirrorPair'],
    disabled: (c) => !mirrorPairOf(c.store, c.node).enabled,
    run: (c) => {
      const s = c.store.getState();
      void s.dispatch([{ name: 'node.mirrorPair', input: { node: c.node, twin: mirrorPairOf(c.store, c.node).twin } }], `mirror ${c.node}`);
    },
  },
];

const VIEW: Entry[] = [
  {
    kind: 'item', id: 'view.frame', label: () => 'Frame', chords: ['tree.frame', 'viewport.frameSelection'],
    run: (c) => { globalThis.dispatchEvent(new CustomEvent(frameSelectionEvent, { detail: { node: c.node } })); },
  },
  {
    kind: 'item', id: 'select.children', label: () => 'Select children',
    run: (c) => {
      const s = c.store.getState();
      s.select({ nodes: [c.node, ...descendantsOf(s.puppet.doc?.puppet.nodes ?? {}, c.node)] });
    },
  },
  {
    kind: 'item', id: 'view.isolate', label: (c) => (c.isolate?.on === true ? 'Show all' : 'Isolate'), chords: ['viewport.isolate'],
    disabled: (c) => c.isolate === undefined, run: (c) => { c.isolate?.toggle(); },
  },
  {
    kind: 'item', id: 'node.hide', label: (c) => (c.view?.isViewHidden(c.node) === true ? 'Show' : 'Hide'), chords: ['tree.viewHide'],
    disabled: (c) => c.view === undefined, run: (c) => { c.view?.toggleViewHidden(c.node); },
  },
];

const RENAME: Entry[] = [
  {
    kind: 'item', id: 'node.rename', label: () => 'Rename', chords: ['tree.rename'], disabled: (c) => c.view === undefined,
    run: (c) => { c.view?.rename(c.node); },
  },
];

const STRUCTURE: Entry[] = [
  { kind: 'item', id: 'node.addChild', label: () => 'Add child', run: (c) => { addChild(c.store, c.node); } },
  { kind: 'item', id: 'node.remove', label: () => 'Remove', chords: ['tree.remove'], run: (c) => { removeNode(c.store, c.node, c.confirm); } },
];

const ELSEWHERE: Entry[] = [
  {
    kind: 'item', id: 'art.open', label: () => 'Open drawable in Art', disabled: (c) => firstDrawable(c.store, c.node) === null || c.store.getState().puppet.id === null,
    run: (c) => {
      const s = c.store.getState();
      if (s.puppet.id === null) return;
      s.navigate({ screen: 'art', puppetId: s.puppet.id });
      s.select({ drawable: firstDrawable(c.store, c.node) });
    },
  },
  {
    kind: 'item', id: 'agent.ask', label: () => 'Ask the agent about this', disabled: (c) => c.store.getState().puppet.id === null,
    run: (c) => { const id = c.store.getState().puppet.id; if (id !== null) c.store.getState().navigate({ screen: 'agent', puppetId: id }); },
  },
];

/** Rename, keying / mirror / pose rows, view rows, add child / remove, then the rows that leave the pose screen. */
export const NODE_ENTRIES: Entry[] = [...RENAME, SEP, ...KEYING, SEP, ...VIEW, SEP, ...STRUCTURE, SEP, ...ELSEWHERE];
